import {
  Badge,
  Box,
  Button,
  Card,
  CollapsibleSection,
  Flex,
  Grid,
  Heading,
  HStack,
  Input,
  Table,
  Text,
  Textarea,
} from "components/ui";
import { Select } from "components/ui/select";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import {
  MdArrowBack,
  MdDelete,
  MdEdit,
  MdPerson,
  MdSave,
} from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import UsagePanel from "views/admin/dashboard/ai-spend/UsagePanel";
import CustomerAuthorizationCard from "./components/CustomerAuthorizationCard";
import {
  CustomerChatSummary,
  CustomerDetail,
  CustomerMemory,
  CustomerRole,
  CustomerWorkflow,
  customerService,
  UpdateCustomerPersonaRequest,
  UpdateCustomerRequest,
} from "services/customerService";
import {
  CustomerAnalyticsResponse,
  dashboardService,
} from "services/dashboardService";

const CustomerDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [personaEditMode, setPersonaEditMode] = useState(false);
  const [personaOpen, setPersonaOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [chatHistory, setChatHistory] = useState<CustomerChatSummary[]>([]);
  const [chatHistoryLoading, setChatHistoryLoading] = useState(false);
  const [chatHistoryError, setChatHistoryError] = useState<string | null>(null);

  const [workflows, setWorkflows] = useState<CustomerWorkflow[]>([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [workflowsOpen, setWorkflowsOpen] = useState(false);

  const [analytics, setAnalytics] = useState<CustomerAnalyticsResponse | null>(
    null,
  );
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);

  const [memories, setMemories] = useState<CustomerMemory[]>([]);
  const [memoriesLoading, setMemoriesLoading] = useState(false);
  const [memoriesOpen, setMemoriesOpen] = useState(false);
  const [deletingMemoryId, setDeletingMemoryId] = useState<string | null>(null);

  const [customerName, setCustomerName] = useState("");
  const [customerSummary, setCustomerSummary] = useState("");
  // Editable role — committed alongside the basic-info save.
  const [pendingRole, setPendingRole] = useState<CustomerRole>("Admin");

  const [toneOfVoice, setToneOfVoice] = useState("");
  const [formalityLevel, setFormalityLevel] = useState("");
  const [emotionalExpression, setEmotionalExpression] = useState("");
  const [directnessIndirectness, setDirectnessIndirectness] = useState("");
  const [stressLevelFrustration, setStressLevelFrustration] = useState("");
  const [patienceUrgency, setPatienceUrgency] = useState("");
  const [confidenceUncertainty, setConfidenceUncertainty] = useState("");
  const [attentionToDetail, setAttentionToDetail] = useState("");
  const [primaryGoals, setPrimaryGoals] = useState("");
  const [engagementType, setEngagementType] = useState("");
  const [decisionStage, setDecisionStage] = useState("");
  const [trustLevelOpenness, setTrustLevelOpenness] = useState("");
  const [cooperationLevel, setCooperationLevel] = useState("");
  const [politenessEmpathy, setPolitenessEmpathy] = useState("");
  const [learningStyle, setLearningStyle] = useState("");
  const [decisionMakingStyle, setDecisionMakingStyle] = useState("");
  const [engagementPattern, setEngagementPattern] = useState("");

  const loadCustomer = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await customerService.getCustomer(id);
      setCustomer(data);

      setCustomerName(data.customerName || "");
      setCustomerSummary(data.customerSummary || "");
      setPendingRole(data.role);

      if (data.persona) {
        setToneOfVoice(data.persona.toneOfVoice || "");
        setFormalityLevel(data.persona.formalityLevel || "");
        setEmotionalExpression(data.persona.emotionalExpression || "");
        setDirectnessIndirectness(data.persona.directnessIndirectness || "");
        setStressLevelFrustration(data.persona.stressLevelFrustration || "");
        setPatienceUrgency(data.persona.patienceUrgency || "");
        setConfidenceUncertainty(data.persona.confidenceUncertainty || "");
        setAttentionToDetail(data.persona.attentionToDetail || "");
        setPrimaryGoals(data.persona.primaryGoals || "");
        setEngagementType(data.persona.engagementType || "");
        setDecisionStage(data.persona.decisionStage || "");
        setTrustLevelOpenness(data.persona.trustLevelOpenness || "");
        setCooperationLevel(data.persona.cooperationLevel || "");
        setPolitenessEmpathy(data.persona.politenessEmpathy || "");
        setLearningStyle(data.persona.learningStyle || "");
        setDecisionMakingStyle(data.persona.decisionMakingStyle || "");
        setEngagementPattern(data.persona.engagementPattern || "");
      }

      setError("");
    } catch (err: any) {
      setError(
        err.response?.data?.error || t("app.customers.details.loadFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerChats = async () => {
    if (!id) return;

    try {
      setChatHistoryLoading(true);
      const chats = await customerService.getCustomerChats(id);
      setChatHistory(chats);
      setChatHistoryError(null);
    } catch (err: any) {
      setChatHistoryError(
        err.response?.data?.error ||
          t("app.customers.details.chatHistory.loadFailed"),
      );
    } finally {
      setChatHistoryLoading(false);
    }
  };

  const loadCustomerWorkflows = async () => {
    if (!id) return;
    try {
      setWorkflowsLoading(true);
      const data = await customerService.getCustomerWorkflows(id);
      setWorkflows(data);
    } catch {
      // Workflows section is optional — silently swallow
    } finally {
      setWorkflowsLoading(false);
    }
  };

  const loadCustomerAnalytics = async (customerId: string) => {
    try {
      setAnalyticsLoading(true);
      const data = await dashboardService.getCustomerAnalytics(customerId, 30);
      setAnalytics(data);
    } catch {
      // Analytics section is optional — silently swallow
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadCustomerMemories = async () => {
    if (!id) return;
    try {
      setMemoriesLoading(true);
      const data = await customerService.getCustomerMemories(id);
      setMemories(data);
    } catch {
      // Memory section is optional — silently swallow
    } finally {
      setMemoriesLoading(false);
    }
  };

  const handleDeleteMemory = async (memoryId: string) => {
    if (!id) return;
    try {
      setDeletingMemoryId(memoryId);
      await customerService.deleteCustomerMemory(id, memoryId);
      setMemories((prev) => prev.filter((m) => m.id !== memoryId));
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("app.customers.details.memorySection.deleteFailed"),
      );
    } finally {
      setDeletingMemoryId(null);
    }
  };

  useEffect(() => {
    if (id) {
      loadCustomer();
      loadCustomerChats();
      loadCustomerWorkflows();
      loadCustomerMemories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (customer?.customerId) {
      loadCustomerAnalytics(customer.customerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.customerId]);

  const handleSaveCustomer = async () => {
    if (!id) return;

    try {
      setSaving(true);
      const updateData: UpdateCustomerRequest = {
        customerName: customerName || undefined,
        customerSummary: customerSummary || undefined,
        role: pendingRole,
      };

      await customerService.updateCustomer(id, updateData);
      await loadCustomer();
      setEditMode(false);
    } catch (err: any) {
      setError(
        err.response?.data?.error || t("app.customers.details.saveFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSavePersona = async () => {
    if (!id) return;

    try {
      setSaving(true);
      const updateData: UpdateCustomerPersonaRequest = {
        toneOfVoice: toneOfVoice || undefined,
        formalityLevel: formalityLevel || undefined,
        emotionalExpression: emotionalExpression || undefined,
        directnessIndirectness: directnessIndirectness || undefined,
        stressLevelFrustration: stressLevelFrustration || undefined,
        patienceUrgency: patienceUrgency || undefined,
        confidenceUncertainty: confidenceUncertainty || undefined,
        attentionToDetail: attentionToDetail || undefined,
        primaryGoals: primaryGoals || undefined,
        engagementType: engagementType || undefined,
        decisionStage: decisionStage || undefined,
        trustLevelOpenness: trustLevelOpenness || undefined,
        cooperationLevel: cooperationLevel || undefined,
        politenessEmpathy: politenessEmpathy || undefined,
        learningStyle: learningStyle || undefined,
        decisionMakingStyle: decisionMakingStyle || undefined,
        engagementPattern: engagementPattern || undefined,
      };

      await customerService.updateCustomerPersona(id, updateData);
      await loadCustomer();
      setPersonaEditMode(false);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("app.customers.details.savePersonaFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Box>
        <Card.Root>
          <Card.Body>
            <Text textAlign="center">{t("app.customers.details.loading")}</Text>
          </Card.Body>
        </Card.Root>
      </Box>
    );
  }

  if (!customer) {
    return (
      <Card.Root>
        <Card.Body>
          <Text textAlign="center" color="red.600">
            {t("app.customers.details.notFound")}
          </Text>
        </Card.Body>
      </Card.Root>
    );
  }

  return (
    <Flex direction="column" w="full" gap={5}>
      <Flex justify="space-between" align="center">
        <HStack>
          <Button
            variant="ghost"
            onClick={() => navigate("/admin/customers")}
            size="sm"
          >
            <MdArrowBack />
          </Button>
          <Heading size="2xl" fontWeight="bold">
            {t("app.customers.details.title")}
          </Heading>
        </HStack>
      </Flex>

      {error && (
        <Box borderRadius="lg" bg="red.100" p={3}>
          <Text fontSize="sm" color="red.700">
            {error}
          </Text>
        </Box>
      )}

      <Card.Root>
        <Card.Body p={6}>
          <Flex justify="space-between" align="center" mb={4}>
            <HStack>
              <Box>
                <MdPerson size={24} />
              </Box>
              <Heading size="md">
                {t("app.customers.details.basicInfo")}
              </Heading>
            </HStack>
            {!editMode && (
              <Button
                variant="outline"
                size="sm"
                colorPalette="blue"
                onClick={() => setEditMode(true)}
              >
                <MdEdit /> {t("app.customers.details.editBasicInfo")}
              </Button>
            )}
            {editMode && (
              <HStack>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditMode(false);
                    setCustomerName(customer.customerName || "");
                    setCustomerSummary(customer.customerSummary || "");
                    setPendingRole(customer.role);
                  }}
                >
                  {t("app.customers.details.cancelEdit")}
                </Button>
                <Button
                  variant="solid"
                  size="sm"
                  colorPalette="blue"
                  onClick={handleSaveCustomer}
                  loading={saving}
                >
                  <MdSave />{" "}
                  {saving
                    ? t("app.customers.details.saving")
                    : t("app.customers.details.saveBasicInfo")}
                </Button>
              </HStack>
            )}
          </Flex>

          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
            <Box>
              <Text fontSize="xs" color="var(--text-secondary-color)" mb={1}>
                {t("app.customers.details.customerId")}
              </Text>
              <Text fontSize="sm" fontFamily="mono">
                {customer.customerId}
              </Text>
            </Box>

            <Box>
              <Text fontSize="xs" color="var(--text-secondary-color)" mb={1}>
                {t("app.customers.details.customerName")}
              </Text>
              {editMode ? (
                <Input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder={t("app.customers.details.customerName")}
                  size="sm"
                />
              ) : (
                <Text fontSize="sm">
                  {customer.customerName || t("app.common.notAvailable")}
                </Text>
              )}
            </Box>

            <Box gridColumn={{ base: "1", md: "1 / -1" }}>
              <Text fontSize="xs" color="var(--text-secondary-color)" mb={1}>
                {t("app.customers.details.customerSummary")}
              </Text>
              {editMode ? (
                <Textarea
                  value={customerSummary}
                  onChange={(e) => setCustomerSummary(e.target.value)}
                  placeholder={t(
                    "app.customers.details.enterSummaryPlaceholder",
                  )}
                  rows={3}
                />
              ) : (
                <Text fontSize="sm">
                  {customer.customerSummary || t("app.common.notAvailable")}
                </Text>
              )}
            </Box>

            <Box>
              <Text fontSize="xs" color="var(--text-secondary-color)" mb={1}>
                {t("app.customers.details.customerEmail")}
              </Text>
              <Text fontSize="sm">
                {customer.customerEmail || t("app.common.notAvailable")}
              </Text>
            </Box>

            <Box>
              <Text fontSize="xs" color="var(--text-secondary-color)" mb={1}>
                {t("app.customers.details.customerCompany")}
              </Text>
              <Text fontSize="sm">
                {customer.customerCompanyName ||
                  customer.customerCompanyId ||
                  t("app.common.notAvailable")}
              </Text>
              {customer.customerCompanyId &&
                customer.customerCompanyName &&
                customer.customerCompanyId !== customer.customerCompanyName && (
                  <Text
                    fontSize="xs"
                    color="var(--text-secondary-color)"
                    mt={1}
                    fontFamily="mono"
                  >
                    {t("app.customers.details.legacyRef", {
                      defaultValue: "Original ref:",
                    })}{" "}
                    {customer.customerCompanyId}
                  </Text>
                )}
            </Box>

            <Box>
              <Text fontSize="xs" color="var(--text-secondary-color)" mb={1}>
                {t("app.customers.details.memberType", {
                  defaultValue: "Member type",
                })}
              </Text>
              {editMode ? (
                <Select
                  items={[
                    {
                      label: t("app.customers.details.memberTypeAdmin", {
                        defaultValue: "Admin",
                      }),
                      value: "Admin",
                    },
                    {
                      label: t("app.customers.details.memberTypeMember", {
                        defaultValue: "Member",
                      }),
                      value: "Member",
                    },
                  ]}
                  value={[pendingRole]}
                  onValueChange={(e: { value: string[] }) =>
                    setPendingRole((e.value[0] as CustomerRole) ?? "Admin")
                  }
                  helperText={t("app.customers.details.memberTypeHelp", {
                    defaultValue:
                      "Admins can manage the customer company (KB, workflows, members) in the Portal. Members are read-only.",
                  })}
                  size="sm"
                />
              ) : (
                <Text fontSize="sm">
                  {customer.role === "Admin"
                    ? t("app.customers.details.memberTypeAdmin", {
                        defaultValue: "Admin",
                      })
                    : t("app.customers.details.memberTypeMember", {
                        defaultValue: "Member",
                      })}
                </Text>
              )}
            </Box>

            <Box>
              <Text fontSize="xs" color="var(--text-secondary-color)" mb={1}>
                {t("app.customers.details.firstSeen")}
              </Text>
              <Text fontSize="sm">{formatDate(customer.createdAt)}</Text>
            </Box>

            <Box>
              <Text fontSize="xs" color="var(--text-secondary-color)" mb={1}>
                {t("app.customers.details.lastUpdated")}
              </Text>
              <Text fontSize="sm">{formatDate(customer.lastUpdatedAt)}</Text>
            </Box>
          </Grid>
        </Card.Body>
      </Card.Root>

      <Card.Root>
        <Card.Body p={6}>
          <Flex justify="space-between" align="center" mb={4}>
            <Heading size="md">
              {t("app.customers.details.chatHistory.title")}
            </Heading>
          </Flex>

          {chatHistoryLoading && (
            <Text fontSize="sm" color="var(--text-secondary-color)">
              {t("app.customers.details.chatHistory.loading")}
            </Text>
          )}

          {!chatHistoryLoading && chatHistoryError && (
            <Text fontSize="sm" color="red.600">
              {chatHistoryError}
            </Text>
          )}

          {!chatHistoryLoading &&
            !chatHistoryError &&
            chatHistory.length === 0 && (
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customers.details.chatHistory.empty")}
              </Text>
            )}

          {!chatHistoryLoading &&
            !chatHistoryError &&
            chatHistory.length > 0 && (
              <Box overflowX="auto">
                <Table.Root size="sm" variant="outline" minW="700px">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>
                        {t("app.customers.details.chatHistory.columns.name")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t(
                          "app.customers.details.chatHistory.columns.messages",
                        )}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t(
                          "app.customers.details.chatHistory.columns.toolExecutions",
                        )}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.customers.details.chatHistory.columns.created")}
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {chatHistory.map((chat) => (
                      <Table.Row
                        key={chat.id}
                        cursor="pointer"
                        onClick={() =>
                          navigate(`/admin/customers/${id}/chats/${chat.id}`)
                        }
                      >
                        <Table.Cell fontWeight="medium">
                          {chat.title ||
                            t("app.customers.details.chatHistory.unnamed")}
                        </Table.Cell>
                        <Table.Cell>{chat.messageCount}</Table.Cell>
                        <Table.Cell>{chat.toolExecutionCount}</Table.Cell>
                        <Table.Cell whiteSpace="nowrap">
                          {formatDate(chat.createdAt)}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            )}
        </Card.Body>
      </Card.Root>

      <CollapsibleSection
        title={t("app.customers.details.analytics.title")}
        isOpen={analyticsOpen}
        onOpenChange={setAnalyticsOpen}
        bodyProps={{ p: 6 }}
      >
        {analyticsLoading ? (
          <Text fontSize="sm" color="var(--text-secondary-color)">
            {t("app.customers.details.analytics.loading")}
          </Text>
        ) : !analytics ? (
          <Text fontSize="sm" color="var(--text-secondary-color)">
            {t("app.customers.details.analytics.noData")}
          </Text>
        ) : (
          <Flex direction="column" gap={6}>
            {/* Session stats */}
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={3}>
                {t("app.customers.details.analytics.sessionStats")}
              </Text>
              <Grid
                templateColumns={{ base: "1fr 1fr", md: "repeat(4, 1fr)" }}
                gap={4}
              >
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={3}
                  textAlign="center"
                >
                  <Text fontSize="2xl" fontWeight="bold">
                    {analytics.sessionStats.totalSessions}
                  </Text>
                  <Text fontSize="xs" color="var(--text-secondary-color)">
                    {t("app.customers.details.analytics.totalSessions")}
                  </Text>
                </Box>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={3}
                  textAlign="center"
                >
                  <Text fontSize="2xl" fontWeight="bold">
                    {analytics.sessionStats.totalMessages}
                  </Text>
                  <Text fontSize="xs" color="var(--text-secondary-color)">
                    {t("app.customers.details.analytics.totalMessages")}
                  </Text>
                </Box>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={3}
                  textAlign="center"
                >
                  <Text fontSize="2xl" fontWeight="bold">
                    {analytics.sessionStats.totalToolsExecuted}
                  </Text>
                  <Text fontSize="xs" color="var(--text-secondary-color)">
                    {t("app.customers.details.analytics.toolsExecuted")}
                  </Text>
                </Box>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={3}
                  textAlign="center"
                >
                  <Text fontSize="2xl" fontWeight="bold">
                    {analytics.sessionStats.totalToolsExecuted > 0
                      ? `${analytics.sessionStats.toolSuccessRate}%`
                      : t("app.common.notAvailable")}
                  </Text>
                  <Text fontSize="xs" color="var(--text-secondary-color)">
                    {t("app.customers.details.analytics.toolSuccessRate")}
                  </Text>
                </Box>
              </Grid>
              <Grid
                templateColumns={{ base: "1fr 1fr", md: "repeat(3, 1fr)" }}
                gap={4}
                mt={4}
              >
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={3}
                  textAlign="center"
                >
                  <Text fontSize="xl" fontWeight="bold">
                    {analytics.sessionStats.averageMessagesPerSession}
                  </Text>
                  <Text fontSize="xs" color="var(--text-secondary-color)">
                    {t("app.customers.details.analytics.avgMessagesPerSession")}
                  </Text>
                </Box>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={3}
                  textAlign="center"
                >
                  <Text fontSize="xl" fontWeight="bold">
                    {analytics.sessionStats.lastActiveAt
                      ? formatDate(analytics.sessionStats.lastActiveAt)
                      : t("app.common.notAvailable")}
                  </Text>
                  <Text fontSize="xs" color="var(--text-secondary-color)">
                    {t("app.customers.details.analytics.lastActive")}
                  </Text>
                </Box>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={3}
                  textAlign="center"
                >
                  <Text
                    fontSize="xl"
                    fontWeight="bold"
                    color={
                      analytics.sessionStats.daysSinceLastActivity > 14
                        ? "red.500"
                        : analytics.sessionStats.daysSinceLastActivity > 7
                          ? "orange.400"
                          : "green.500"
                    }
                  >
                    {analytics.sessionStats.daysSinceLastActivity >= 0
                      ? `${analytics.sessionStats.daysSinceLastActivity}d`
                      : t("app.common.notAvailable")}
                  </Text>
                  <Text fontSize="xs" color="var(--text-secondary-color)">
                    {t("app.customers.details.analytics.daysSinceLastActivity")}
                  </Text>
                </Box>
              </Grid>
            </Box>

            {/* Engagement funnel */}
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={3}>
                {t("app.customers.details.analytics.engagementFunnel")}
              </Text>
              <Flex direction="column" gap={2}>
                {analytics.funnel.map((stage, index) => (
                  <Box key={stage.stageName}>
                    <Flex justify="space-between" align="center" mb={1}>
                      <Text fontSize="sm">{stage.stageName}</Text>
                      <HStack gap={3}>
                        <Text fontSize="sm" fontWeight="semibold">
                          {stage.count}
                        </Text>
                        <Text
                          fontSize="xs"
                          color="var(--text-secondary-color)"
                          minW="60px"
                          textAlign="right"
                        >
                          {index === 0
                            ? "100%"
                            : `${stage.conversionRate.toFixed(1)}%`}
                        </Text>
                      </HStack>
                    </Flex>
                    <Box
                      h="6px"
                      bg="var(--card-bg-color, #e2e8f0)"
                      borderRadius="full"
                      overflow="hidden"
                    >
                      <Box
                        h="100%"
                        w={`${Math.min(stage.overallConversionRate, 100)}%`}
                        bg={
                          index === 0
                            ? "blue.500"
                            : index === 1
                              ? "teal.400"
                              : index === 2
                                ? "green.400"
                                : "purple.400"
                        }
                        borderRadius="full"
                        transition="width 0.4s ease"
                      />
                    </Box>
                  </Box>
                ))}
              </Flex>
            </Box>
          </Flex>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        title={t("app.customers.details.usage.title")}
        description={t("app.customers.details.usage.description")}
        isOpen={usageOpen}
        onOpenChange={setUsageOpen}
        bodyProps={{ p: 6 }}
      >
        {/* WP-70: the customer's own AI usage and the allowance their company draws on. The
            panel only queries once the section is opened — it aggregates the largest table. */}
        {usageOpen && customer.customerId && (
          <UsagePanel
            filter={{ customerId: customer.customerId }}
            sections={["allowance", "bySource", "byWorkflow", "topJobs"]}
            compact
          />
        )}
      </CollapsibleSection>

      <CollapsibleSection
        title={t("app.customers.details.memory")}
        isOpen={memoriesOpen}
        onOpenChange={setMemoriesOpen}
        bodyProps={{ p: 6 }}
      >
        {memoriesLoading ? (
          <Text fontSize="sm" color="var(--text-secondary-color)">
            {t("app.customers.details.memorySection.loading")}
          </Text>
        ) : memories.length === 0 ? (
          <Text fontSize="sm" color="var(--text-secondary-color)">
            {t("app.customers.details.memorySection.empty")}
          </Text>
        ) : (
          <Flex direction="column" gap={6}>
            {Object.entries(
              memories.reduce<Record<string, CustomerMemory[]>>(
                (groups, memory) => {
                  const key =
                    memory.category ||
                    t("app.customers.details.memorySection.uncategorized");
                  (groups[key] = groups[key] || []).push(memory);
                  return groups;
                },
                {},
              ),
            ).map(([category, items]) => (
              <Box key={category}>
                <Text fontSize="sm" fontWeight="semibold" mb={3}>
                  {category}
                </Text>
                <Flex direction="column" gap={2}>
                  {items.map((memory) => (
                    <Flex
                      key={memory.id}
                      borderWidth="1px"
                      borderRadius="md"
                      p={3}
                      gap={3}
                      align="flex-start"
                      justify="space-between"
                    >
                      <Box flex="1">
                        <Text fontSize="sm" mb={2}>
                          {memory.content}
                        </Text>
                        <HStack gap={2}>
                          <Badge colorPalette="purple" variant="subtle">
                            {memory.category ||
                              t(
                                "app.customers.details.memorySection.uncategorized",
                              )}
                          </Badge>
                          <Text
                            fontSize="xs"
                            color="var(--text-secondary-color)"
                          >
                            {t("app.customers.details.memorySection.salience")}:{" "}
                            {memory.salience}
                          </Text>
                        </HStack>
                      </Box>
                      <Button
                        variant="outline"
                        size="sm"
                        colorPalette="red"
                        onClick={() => handleDeleteMemory(memory.id)}
                        loading={deletingMemoryId === memory.id}
                      >
                        <MdDelete />{" "}
                        {t("app.customers.details.memorySection.delete")}
                      </Button>
                    </Flex>
                  ))}
                </Flex>
              </Box>
            ))}
          </Flex>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        title={t("app.customers.details.persona")}
        isOpen={personaOpen}
        onOpenChange={setPersonaOpen}
        bodyProps={{ p: 6 }}
      >
        <Flex justify="space-between" align="center" mb={4}>
          <HStack>
            {customer.hasPersona && (
              <Badge colorPalette="green" variant="solid">
                {t("app.common.active")}
              </Badge>
            )}
          </HStack>
          {!personaEditMode && (
            <Button
              variant="outline"
              size="sm"
              colorPalette="blue"
              onClick={() => setPersonaEditMode(true)}
            >
              <MdEdit /> {t("app.customers.details.editPersona")}
            </Button>
          )}
          {personaEditMode && (
            <HStack>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPersonaEditMode(false);
                  if (customer.persona) {
                    setToneOfVoice(customer.persona.toneOfVoice || "");
                    setFormalityLevel(customer.persona.formalityLevel || "");
                    // ... reset other fields
                  }
                }}
              >
                {t("app.customers.details.cancelEdit")}
              </Button>
              <Button
                variant="solid"
                size="sm"
                colorPalette="blue"
                onClick={handleSavePersona}
                loading={saving}
              >
                <MdSave />{" "}
                {saving
                  ? t("app.customers.details.saving")
                  : t("app.customers.details.savePersona")}
              </Button>
            </HStack>
          )}
        </Flex>

        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
          {[
            { key: "toneOfVoice", value: toneOfVoice, setter: setToneOfVoice },
            {
              key: "formalityLevel",
              value: formalityLevel,
              setter: setFormalityLevel,
            },
            {
              key: "emotionalExpression",
              value: emotionalExpression,
              setter: setEmotionalExpression,
            },
            {
              key: "directnessIndirectness",
              value: directnessIndirectness,
              setter: setDirectnessIndirectness,
            },
            {
              key: "stressLevelFrustration",
              value: stressLevelFrustration,
              setter: setStressLevelFrustration,
            },
            {
              key: "patienceUrgency",
              value: patienceUrgency,
              setter: setPatienceUrgency,
            },
            {
              key: "confidenceUncertainty",
              value: confidenceUncertainty,
              setter: setConfidenceUncertainty,
            },
            {
              key: "attentionToDetail",
              value: attentionToDetail,
              setter: setAttentionToDetail,
            },
            {
              key: "engagementType",
              value: engagementType,
              setter: setEngagementType,
            },
            {
              key: "decisionStage",
              value: decisionStage,
              setter: setDecisionStage,
            },
            {
              key: "trustLevelOpenness",
              value: trustLevelOpenness,
              setter: setTrustLevelOpenness,
            },
            {
              key: "cooperationLevel",
              value: cooperationLevel,
              setter: setCooperationLevel,
            },
            {
              key: "politenessEmpathy",
              value: politenessEmpathy,
              setter: setPolitenessEmpathy,
            },
            {
              key: "learningStyle",
              value: learningStyle,
              setter: setLearningStyle,
            },
            {
              key: "decisionMakingStyle",
              value: decisionMakingStyle,
              setter: setDecisionMakingStyle,
            },
            {
              key: "engagementPattern",
              value: engagementPattern,
              setter: setEngagementPattern,
            },
          ].map((field) => (
            <Box key={field.key}>
              <Text fontSize="xs" color="var(--text-secondary-color)" mb={1}>
                {t(`app.customers.details.personaFields.${field.key}`)}
              </Text>
              {personaEditMode ? (
                <Input
                  value={field.value}
                  onChange={(e) => field.setter(e.target.value)}
                  placeholder={t(
                    `app.customers.details.personaFields.${field.key}`,
                  )}
                  size="sm"
                />
              ) : (
                <Text fontSize="sm">
                  {field.value || t("app.common.notAvailable")}
                </Text>
              )}
            </Box>
          ))}

          <Box gridColumn={{ base: "1", md: "1 / -1" }}>
            <Text fontSize="xs" color="var(--text-secondary-color)" mb={1}>
              {t("app.customers.details.personaFields.primaryGoals")}
            </Text>
            {personaEditMode ? (
              <Textarea
                value={primaryGoals}
                onChange={(e) => setPrimaryGoals(e.target.value)}
                placeholder={t(
                  "app.customers.details.personaFields.primaryGoals",
                )}
                rows={3}
              />
            ) : (
              <Text fontSize="sm">
                {primaryGoals || t("app.common.notAvailable")}
              </Text>
            )}
          </Box>
        </Grid>
      </CollapsibleSection>

      <CustomerAuthorizationCard
        customerId={customer.id}
        formatDate={formatDate}
      />

      {/* Workflows & Schedules */}
      <CollapsibleSection
        title={t("app.customers.details.workflows.title")}
        isOpen={workflowsOpen}
        onOpenChange={setWorkflowsOpen}
      >
        {workflowsLoading ? (
          <Text fontSize="sm" color="var(--text-secondary-color)">
            {t("app.common.loading", { defaultValue: "Loading..." })}
          </Text>
        ) : workflows.length === 0 ? (
          <Text fontSize="sm" color="var(--text-secondary-color)">
            {t("app.customers.details.workflows.empty")}
          </Text>
        ) : (
          <Flex direction="column" gap={4}>
            {workflows.map((wf) => (
              <Box key={wf.id} borderWidth="1px" borderRadius="md" p={4}>
                <HStack justify="space-between" mb={2}>
                  <Heading size="sm">{wf.name}</Heading>
                  <Badge colorPalette={wf.isActive ? "green" : "gray"}>
                    {wf.isActive
                      ? t("app.customers.details.workflows.active")
                      : t("app.customers.details.workflows.inactive")}
                  </Badge>
                </HStack>
                <Text fontSize="sm" color="var(--text-secondary-color)" mb={3}>
                  {wf.description}
                </Text>

                {wf.schedules.length > 0 && (
                  <Box mb={3}>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      mb={1}
                      color="var(--text-secondary-color)"
                    >
                      {t("app.customers.details.workflows.schedules")}
                    </Text>
                    <Flex direction="column" gap={1}>
                      {wf.schedules.map((s) => (
                        <Text key={s.id} fontSize="xs">
                          {s.frequencyType} · {s.timeOfDay.substring(0, 5)} ·{" "}
                          {s.timezone}
                          {s.nextRunAt
                            ? ` · ${t(
                                "app.customers.details.workflows.nextRun",
                              )}: ${formatDate(s.nextRunAt)}`
                            : ""}
                        </Text>
                      ))}
                    </Flex>
                  </Box>
                )}

                {wf.recentLogs.length > 0 && (
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      mb={1}
                      color="var(--text-secondary-color)"
                    >
                      {t("app.customers.details.workflows.recentRuns")}
                    </Text>
                    <Table.Root size="sm" variant="outline">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>
                            {t("app.customers.details.workflows.runStarted")}
                          </Table.ColumnHeader>
                          <Table.ColumnHeader>
                            {t("app.customers.details.workflows.runStatus")}
                          </Table.ColumnHeader>
                          <Table.ColumnHeader>
                            {t("app.customers.details.workflows.runSession")}
                          </Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {wf.recentLogs.map((log) => (
                          <Table.Row key={log.id}>
                            <Table.Cell fontSize="xs">
                              {formatDate(log.startedAt)}
                            </Table.Cell>
                            <Table.Cell>
                              <Badge
                                colorPalette={
                                  log.status === "Completed"
                                    ? "green"
                                    : log.status === "Failed"
                                      ? "red"
                                      : log.status === "Running"
                                        ? "blue"
                                        : log.status === "Cancelled"
                                          ? "orange"
                                          : "yellow"
                                }
                                size="sm"
                              >
                                {log.status}
                              </Badge>
                            </Table.Cell>
                            <Table.Cell fontSize="xs" fontFamily="mono">
                              {log.chatSessionId
                                ? log.chatSessionId.substring(0, 8) + "…"
                                : "-"}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Box>
                )}
              </Box>
            ))}
          </Flex>
        )}
      </CollapsibleSection>
    </Flex>
  );
};

export default CustomerDetails;
